import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check } from "lucide-react";

/**
 * @typedef {Object} SuccessDialogComponent
 * @property {() => void} openDialog
 * @property {() => void} closeDialog
 */

/**
 * @param {object} props
 * @param {string} [props.title="Success"]
 * @param {string} [props.message="Operation completed successfully"]
 * @param {string} [props.buttonText="Continue"]
 * @param {string} [props.resourceName="resource"]
 * @param {() => void} [props.onClose]
 * @param {React.ForwardedRef<SuccessDialogComponent>} ref
 */
const SuccessDialog = forwardRef(function SuccessDialog(
  { title = "Success", message = "Operation completed successfully", buttonText = "Continue", onClose },
  ref
) {
  const [open, setOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    openDialog: () => setOpen(true),
    closeDialog: () => setOpen(false),
  }));

  function handleClose() {
    setOpen(false);
    onClose?.();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="flex flex-col items-center">
        <div className="flex justify-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-500">
            <Check className="h-6 w-6 text-white" />
          </div>
        </div>

        <DialogHeader className="text-center gap-0">
          <DialogTitle className="text-center text-lg font-semibold">{title}</DialogTitle>
          <DialogDescription className="mt-2 text-center mx-auto sm:max-w-[90%] text-sm text-muted-foreground">
            {message}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center w-full">
          <DialogClose asChild>
            <Button className="w-full" onClick={handleClose}>
              {buttonText}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default SuccessDialog;
